import { Component, inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { PaymentSubmitButtonComponent } from '@components/consultation-request/payment-submit-button/payment-submit-button.component';
import { ErrorNotificationComponent } from '@components/error-notification/error-notification.component';
import { TestCenterComponent } from '@components/test-center/test-center.component';
import { StorageKeys } from '@enums/storage-keys';
import { environment } from '@environments/environment';
import { InHomeCollection } from '@interfaces/in-home-collection';
import { InHomeCollectionService, Result, ResultService } from '@Medology/ng-findalab';
import {
  ConfigService,
  FormLayout,
  NgxPaymentFormComponent,
  Payment,
  PaymentTypes,
  providePaymentConfig,
} from '@medology/ngx-payment';
import { ConsultationRequestOrderDetail } from '@models/consultation-request/consultation-request-order-detail';
import { Recharge } from '@models/recharge';
import { PaymentRechargeBraintreeStoreRequest } from '@models/requests/payment-recharge-briantree-store-request.model';
import { ConsultationRequestService } from '@services/consultation-request.service';
import { ConsultationStorageService } from '@services/consultation-storage.service';
import { DocumentService } from '@services/document.service';
import { ErrorHandlerService } from '@services/error-handler.service';
import { ErrorLoggingService } from '@services/error-logging.service';
import { NavigationService } from '@services/navigation.service';
import { OrderService } from '@services/order.service';
import { OrderPaymentService } from '@services/order-payment.service';
import { PaymentService } from '@services/payment.service';
import { StorageService } from '@services/storage.service';
import { catchError, concatMap, EMPTY, finalize, of, Subscription, throwError } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-choose-a-lab',
  templateUrl: './choose-a-lab.component.html',
  styleUrls: ['./choose-a-lab.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [TestCenterComponent, ErrorNotificationComponent, NgxPaymentFormComponent, PaymentSubmitButtonComponent],
  providers: [
    providePaymentConfig({
      environment: environment.production ? 'production' : 'development',
      braintreeToken: environment.braintreeToken,
      googlePayConfig: {
        merchantId: environment.googlePay.merchantId,
      },
      enabled: [
        PaymentTypes.CreditCard,
        PaymentTypes.PayPal,
        PaymentTypes.Venmo,
        PaymentTypes.ApplePay,
        PaymentTypes.GiftCard,
        PaymentTypes.GooglePay,
      ],
    }),
  ],
})
export class ChooseALabComponent implements OnInit, OnDestroy {
  @ViewChild('ngxPaymentForm', { static: false }) public ngxPaymentFormComponent: NgxPaymentFormComponent;

  /**
   * Order hash from query parameters
   */
  public hash: string = '';

  /**
   * Error message to display if lab update fails
   */
  public updateError: string = '';

  /**
   * Flag to indicate if the update was successful
   */
  public labUpdated: boolean = false;

  public showPaymentForm: boolean = false;

  public isInHomeCollection: boolean = false;

  public payments: Payment[] = [];
  public processing: boolean = false;
  public submissionErrors: string[] = [];
  private orderDetails: ConsultationRequestOrderDetail | null = null;
  private storage = inject(StorageService);
  private orderService = inject(OrderService);
  private resultService = inject(ResultService);
  private documentService = inject(DocumentService);
  private route = inject(ActivatedRoute);
  private navigationService = inject(NavigationService);
  private consultationStorageService = inject(ConsultationStorageService);
  private consultationRequestService = inject(ConsultationRequestService);
  private orderPaymentService = inject(OrderPaymentService);
  private errorLoggingService = inject(ErrorLoggingService);
  private errorHandlerService = inject(ErrorHandlerService);
  private paymentService = inject(PaymentService);
  private paymentConfigService = inject(ConfigService);
  private inHomeService = inject(InHomeCollectionService);

  /**
   * Subscription to lab selection
   */
  private labSelectionSubscription: Subscription;

  public constructor() {
    // Default settings, if we ever need a different configuration we should probably make this a route param
    this.storage.betterLab = true;
  }

  /**
   * Determines if in-home collection is allowed based on the application configuration.
   */
  public get inHomeCollectionAllowed(): boolean {
    return environment.ngFindalab.inHomeCollectionAllowed;
  }

  /**
   * Returns in home collection cost in cents.
   */
  public get inHomeCollectionCostInCents(): number {
    return environment.ngFindalab.inHomeCollectionCostInCents;
  }

  /**
   * Gets the patient's name from the consultation storage service or order details, or returns an empty string if not available.
   */
  public get patientName(): string {
    const patientNameFromOrder = this.orderDetails
      ? `${this.orderDetails.first_name} ${this.orderDetails.last_name}`
      : '';

    return this.consultationStorageService.patient?.name || patientNameFromOrder;
  }

  /**
   * Get the in-home collection information from the booking result stored in the storage service.
   */
  public get inHomeCollectionFromBookingResult(): InHomeCollection | null {
    const bookingResult = this.storage.inHomeBookingResult;
    if (!bookingResult) {
      return null;
    }

    return {
      appointment_key: bookingResult.bookingSlot.key,
      street: bookingResult.address.street,
      city: bookingResult.address.city,
      state: bookingResult.address.state,
      zip_code: bookingResult.address.zipCode,
      provider_id: bookingResult.bookingSlot.providerId,
    };
  }

  /*
   * Gets the tests from storage.
   */
  public get tests() {
    return this.storage.tests;
  }

  /**
   * Returns the contact email for the site.
   */
  public get contactEmail(): string {
    return environment.email;
  }

  /**
   * Gets the brand-specified order form layout property.
   *
   * Either "stacked" or "horizontal", see NgxPayment's `FormLayout` enum.
   */
  public get orderPaymentFormLayout(): FormLayout {
    return environment.order.orderPaymentFormLayout as FormLayout;
  }

  /**
   * Gets the recharge information from the consultation storage service or order details, or returns null if not available.
   */
  private get recharge(): Recharge {
    return this.consultationStorageService.recharge || this.orderDetails?.recharge || null;
  }

  /**
   * Gets the payment recharge request object for Braintree payment.
   */
  private get rechargeBrainTreeRequest(): PaymentRechargeBraintreeStoreRequest {
    return new PaymentRechargeBraintreeStoreRequest(
      this.storage.transactionId,
      this.inHomeCollectionCostInCents,
      this.recharge?.vault_id,
    );
  }

  public ngOnInit(): void {
    this.updatePaymentConfig();
    this.storage.providerIds = environment.ngFindalab.inHomeCollectionProviderIds;
    this.storage.transactionId =
      this.route.snapshot.queryParams['order_id'] || this.consultationStorageService.transactionId;
    this.hash = this.route.snapshot.queryParams['hash'] || this.consultationStorageService.hash;

    if (!this.consultationStorageService.recharge) {
      this.consultationRequestService
        .getOrderDetails(this.storage.transactionId, this.hash)
        .subscribe((response) => (this.orderDetails = response.body));
    }

    this.orderService.labResultSubscription?.unsubscribe();

    this.labSelectionSubscription = this.resultService.resultSelected$.subscribe((result: Result) =>
      this.onLabUpdate(result),
    );
  }

  public ngOnDestroy() {
    this.cleanup();
  }

  /**
   * Handles manual payment submitted.
   */
  public onPaymentSubmit(): void {
    this.submissionErrors = [];
    this.processing = true;

    const [initialRequest, followUpRequest] = this.paymentService.getPaymentRequests(
      this.payments,
      this.inHomeCollectionCostInCents,
      this.storage.transactionId,
      this.patientName,
    );

    this.orderPaymentService
      .store(initialRequest, this.hash)
      .pipe(
        switchMap(() =>
          followUpRequest
            ? this.orderPaymentService
                .store(followUpRequest, this.hash)
                .pipe(catchError((error) => this.orderService.swallowError(error)))
            : of(null),
        ),
        switchMap(() => this.handleIhcSchedulingObservable()),
        finalize(() => (this.processing = false)),
      )
      .subscribe({
        next: () => {
          this.showPaymentForm = false;
          this.onSuccessfulTestCenterSelection();
        },
        error: (error) => (this.submissionErrors = this.errorHandlerService.handleConsultationSubmissionError(error)),
      });
  }

  /**
   * Redirects to the next consultation request page if in a consultation request workflow.
   */
  public inHomeCollectionUpdate(): void {
    if (this.storage.inHomeBookingResult === null) {
      return;
    }

    this.updateError = '';

    this.handleIhcRechargePaymentObservable()
      .pipe(concatMap(() => this.handleIhcSchedulingObservable()))
      .subscribe({
        next: () => this.onSuccessfulTestCenterSelection(),
        error: (error) => {
          this.inHomeService.toggleScheduling(false);
          this.updateError =
            error.error?.message || 'An error occurred while scheduling your appointment. Please try again.';
          this.documentService.scrollToBottom();
        },
      });
  }

  /**
   * Handles payment selection from the payment form.
   */
  public onPaymentsSelected(payments: Payment[]): void {
    this.payments = payments;
  }

  /**
   * Returns observable for recharge payment if needed, otherwise returns observable of null.
   * If payment is needed, attempts to process the payment and returns an error if it fails.
   */
  private handleIhcRechargePaymentObservable() {
    if (this.storage.isInHomeCollectionPaid) {
      return of(null);
    }

    if (!this.recharge?.vault_id) {
      this.showPaymentForm = true;
      return EMPTY;
    }

    return this.orderPaymentService.store(this.rechargeBrainTreeRequest, this.hash).pipe(
      catchError((error) => {
        if (!this.errorHandlerService.isCreditCardDeclineError(error)) {
          this.errorLoggingService.logHttpError(error, 'Failed to submit - Choose a Lab IHC/Payment', {
            rechargeAttempted: true,
            consultationId: this.consultationStorageService.consultationId,
          });
        }
        this.showPaymentForm = true;

        return throwError(() => error);
      }),
    );
  }

  /**
   * Returns observable for scheduling the in-home collection appointment.
   */
  private handleIhcSchedulingObservable() {
    this.storage.isInHomeCollectionPaid = true;
    this.isInHomeCollection = true;
    return this.orderService.storeInHomeCollectionAppointment(
      this.storage.transactionId,
      this.hash,
      this.inHomeCollectionFromBookingResult,
    );
  }

  /**
   * Method to handle successful test center selection or in-home collection scheduling, navigating to the next page in
   * the consultation request workflow if applicable.
   */
  private onSuccessfulTestCenterSelection(): void {
    this.labUpdated = true;
    if (this.consultationStorageService.consultationId) {
      this.navigationService.navigateToNextConsultationRequestPage(this.consultationStorageService.treatmentType);
    }

    this.cleanup();
  }

  /**
   * Handle lab selection and update
   *
   * @param {Result} result The selected lab result
   */
  private onLabUpdate(result: Result): void {
    this.updateError = '';

    this.orderService.updateLab(result.id, this.hash).subscribe({
      next: () => {
        this.storage.betterLab = false;
        this.onSuccessfulTestCenterSelection();
      },
      error: (error) => this.onErrorLabUpdate(error),
    });
  }

  /**
   * Handle lab update error
   *
   * @param {any} error The error response
   */
  private onErrorLabUpdate(error: any): void {
    this.updateError = error.error?.message || 'An error occurred while updating the lab. Please try again.';
    this.documentService.scrollToBottom();
  }

  /**
   * Updates the to the ngx-payment payment config during init.
   */
  private updatePaymentConfig(): void {
    this.paymentService.setGiftCardConfig(this.paymentConfigService);
  }

  /**
   * Method to perform cleanup.
   */
  private cleanup(): void {
    this.labSelectionSubscription?.unsubscribe();
    this.storage.remove(StorageKeys.TransactionId, false);
    this.storage.remove(StorageKeys.Center);
    this.storage.remove(StorageKeys.BetterLab);
    this.storage.remove(StorageKeys.ProviderIds);
    this.storage.remove(StorageKeys.ZipCode);
    this.storage.remove(StorageKeys.IsIhcPaid);

    if (!this.consultationStorageService.consultationId) {
      this.storage.remove(StorageKeys.InHomeBookingResult);
    }
  }
}
